import {ManagerHistory} from "./ManagerHistory";
import {Operation} from "./Operation";
import {CJMapManager} from "./CJmapManager";
import {debug_log} from "../utils/debug";

type SequentialOperation = { type: string, data: string, seq: number };

export class SequenceManager {

    private mgr_history: ManagerHistory | null = null;
    private last_seq: number = 0;
    private applied: Map<number, { mgr: CJMapManager, op: Operation }> = new Map<number, { mgr: CJMapManager; op: Operation }>();
    private scheduled_ops: SequentialOperation[] = [];

    private current_mgr: CJMapManager = CJMapManager.createNew();

    public mutex: Mutex = new Mutex();

    get LastSeq(): number {
        return this.last_seq;
    }

    get Mutex(): Mutex {
        return this.mutex;
    }

    initialize(mgr_history: ManagerHistory) {
        this.mgr_history = mgr_history;
        this.current_mgr = mgr_history.LastManager;
        this.last_seq = 0;
        this.applied.clear();

        this.scheduled_ops = [];
    }

    put(mgr: CJMapManager, sequential_op: SequentialOperation) {
        const op = Operation.fromStoredData(sequential_op);
        this.applied.set(sequential_op.seq, {mgr, op});
        if (this.last_seq < sequential_op.seq) {
            this.last_seq = sequential_op.seq;
        }
    }

    run(sequential_op: SequentialOperation): CJMapManager {
        if (this.mgr_history === null) {
            throw new Error("no mgr_history");
        }

        if (this.last_seq < sequential_op.seq) {
            const op = Operation.fromStoredData(sequential_op);
            const mgr = this.mgr_history.LastManager;
            this.mgr_history.run(mgr, op);

            this.applied.set(sequential_op.seq, {mgr, op});
            this.last_seq = sequential_op.seq;
            return this.mgr_history.LastManager;
        }


        if (this.applied.has(sequential_op.seq)) {
            // 適応済み
            return this.mgr_history.LastManager;
        } else {

            const mgr = this.findPreviousManagerOrNull(sequential_op.seq);
            if (mgr === null) {
                throw new Error("no previous manager");
            }

            const op = Operation.fromStoredData(sequential_op);
            this.mgr_history.run(mgr, op);
            this.applied.set(sequential_op.seq, {mgr, op});


            for (const {op} of this.findNextManagerAndOp(sequential_op.seq)) {
                const mgr = this.mgr_history.LastManager;
                this.mgr_history.run(mgr, op);
                this.applied.set(sequential_op.seq, {mgr, op});
            }

            return this.mgr_history.LastManager;
        }


    }

    private findPreviousManagerOrNull(seq: number) {
        while (0 < --seq) {
            const mgr_op = this.applied.get(seq);
            if (mgr_op !== undefined) {
                return mgr_op.mgr;
            }
        }

        return null;
    }

    private findNextManagerAndOp(seq: number) {
        const next_mgr_op: { mgr: CJMapManager, op: Operation }[] = [];
        while (++seq <= this.last_seq) {
            const mgr_op = this.applied.get(seq);
            if (mgr_op !== undefined) {
                next_mgr_op.push(mgr_op);
            }
        }

        return next_mgr_op;
    }

    scheduleOps(ops: SequentialOperation[]) {
        const new_ops = Array.from(this.scheduled_ops).concat(ops);
        new_ops.sort((a, b) => a.seq - b.seq); // 昇順

        this.scheduled_ops = new_ops;

    }

    applyOps(): CJMapManager | null {
        if (this.mgr_history === null) {
            return null;
        }

        if (this.scheduled_ops.length === 0) {
            return null;
        }

        const apply_ops = this.scheduled_ops;
        this.scheduled_ops = [];

        debug_log("applyOps:", apply_ops);

        const current_selection = this.current_mgr.Selection;

        for (const op of apply_ops) {
            this.run(op);
        }

        return this.mgr_history.LastManager.restoreSelection(current_selection);
    }

    setCurrentMgr(mgr: CJMapManager) {
        this.current_mgr = mgr;
    }
}

class Mutex {

    private lock_count: number = 0;

    async runAsyncIfUnlocked(callable: () => Promise<any>) {
        if (this.lock_count === 0) {
            await callable();
        }
    }

    async runAsyncAfterUnlocked(callable: () => Promise<any>) {

        while (this.lock_count !== 0) {
            await new Promise(r => {setTimeout(r, 10)});
        }

        await callable();
    }


    async lockAsync<T>(callable: () => T): Promise<T> {
        ++this.lock_count;
        const ret = await callable();
        --this.lock_count;
        return ret;
    }

    lock(callable: () => Promise<any>): void {
        ++this.lock_count;
        callable().finally(() => {
            --this.lock_count;
        });
    }

}
